import { api } from './api';

// ==================== DTOs ====================

export interface ChallengeDto {
  id: string;
  title: string;
  description: string;
  disciplines: string[];
  duration: number;
  durationType: string;
  difficulty: string;
  createdById?: string;
  isSystem: boolean;
  isPublic: boolean;
  participantCount: number;
  completionCount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface ChallengeParticipationDto {
  id: string;
  challengeId: string;
  userId: string;
  challengeTitle: string;
  status: string;
  joinedAt: string;
  completedAt?: string;
  abandonedAt?: string;
  resultText?: string;
  resultMediaUrls: string[];
  durationMinutes?: number;
  createdAt: string;
  updatedAt?: string;
}

// ==================== Request Types ====================

export interface CreateCustomChallengeRequest {
  title: string;
  description: string;
  disciplines: string[];
  duration: number;
  durationType: string;
  difficulty: string;
}

export interface CompleteChallengeRequest {
  resultText?: string;
  resultMediaUrls?: string[];
  durationMinutes?: number;
}

// ==================== Service ====================

export const challengesService = {
  // Library
  async getChallengeLibrary(options?: {
    discipline?: string;
    difficulty?: string;
    search?: string;
  }): Promise<ChallengeDto[]> {
    const params = new URLSearchParams();
    if (options?.discipline) params.append('discipline', options.discipline);
    if (options?.difficulty) params.append('difficulty', options.difficulty);
    if (options?.search) params.append('search', options.search);
    const queryString = params.toString();
    return api.get<ChallengeDto[]>(`/challenges${queryString ? `?${queryString}` : ''}`);
  },

  async getChallengeById(id: string): Promise<ChallengeDto> {
    return api.get<ChallengeDto>(`/challenges/${id}`);
  },

  async createCustomChallenge(request: CreateCustomChallengeRequest): Promise<ChallengeDto> {
    return api.post<ChallengeDto>('/challenges', request);
  },

  // Participation
  async joinChallenge(id: string): Promise<ChallengeParticipationDto> {
    return api.post<ChallengeParticipationDto>(`/challenges/${id}/join`, {});
  },

  async completeChallenge(id: string, request?: CompleteChallengeRequest): Promise<ChallengeParticipationDto> {
    return api.post<ChallengeParticipationDto>(`/challenges/${id}/complete`, request || {});
  },

  async abandonChallenge(id: string): Promise<ChallengeParticipationDto> {
    return api.post<ChallengeParticipationDto>(`/challenges/${id}/abandon`, {});
  },

  // Results
  async getChallengeResults(id: string, pageNumber = 1, pageSize = 20): Promise<ChallengeParticipationDto[]> {
    return api.get<ChallengeParticipationDto[]>(`/challenges/${id}/results?pageNumber=${pageNumber}&pageSize=${pageSize}`);
  },

  // User's challenges
  async getActiveChallenges(): Promise<ChallengeParticipationDto[]> {
    return api.get<ChallengeParticipationDto[]>('/challenges/mine');
  },

  async getCompletedChallenges(): Promise<ChallengeParticipationDto[]> {
    return api.get<ChallengeParticipationDto[]>('/challenges/completed');
  },
};
